// Order sets, apply side (PROD-2712): the STATE. Owns every set applied to this visit, the
// client-side origin of each order a set placed (orderSetId · application · itemId · edited), the
// receipt, and the AC5 measurement. Called unconditionally by the encounter page (rules of hooks);
// with `enabled` false it fetches nothing, holds nothing, and every handler is a no-op, so the
// flag-off page behaves exactly as before.
//
// SAME PATH AS A SINGLE ORDER. Every order a set places goes through `attachOrder`, the page's
// `handleAttachOrder` (the function an order dialog's confirm reaches when a clinician adds one
// order from the rail), built by the SAME mapper a reload uses (see order-set-apply.ts). Medications
// then run the same interaction check the dose dialog runs (see AppliedMedicationChecks in
// order-set-canvas-blocks.tsx). Nothing is sent or billed here: the orders are unsigned drafts on
// the canvas, saved with the assessment like any other, and sent only at Sign.
//
// The origin map is CLIENT-SIDE ONLY. The encounter save payload is untouched (the backend does not
// accept order-set fields on the assessment), so an order's set origin lasts for this session.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { DxCard, PlanOrder } from '../lib/to-patient-assessment';
import type { CodedOrderPayload, OrderPayloadMap } from '../lib/order-payload';
import type { EducationArticle } from '../lib/to-education-article';
import type { OrderSetDiagnosis } from '@/types/order-set-api';
import type { OrderSetApplyDetail, OrderSetApplyItem, OrderSetResolution } from '@/types/order-set-apply-api';
import {
  applyReceipt,
  decideTargetDx,
  mintOrderSetOrderId,
  orderSetItemToArticle,
  orderSetItemToDraft,
  planOrderSetApplication,
  suggestedDxAsSearchResult,
  type DraftOrder,
  type DuplicateItem,
  type ListedItem,
  type NotOrderableItem,
} from './order-set-apply';
import { findDuplicateOrders, sameArticle } from './order-set-duplicates';
import { fetchOrderSetForApply, startOrderSetTelemetry, type OrderSetTelemetry } from './order-set-apply-service';
import { consolidateLeftovers, followResolvedDiagnosis, liveApplications } from './order-set-applications';
import type { ArticleDxLink } from './ap-dx-groups';

/** Where an order placed by a set came from. Kept on the client, keyed by the draft's order id. */
export interface OrderSetOrigin {
  orderSetId: string;
  /** The local application key (the server's applicationId is resolved by its telemetry). */
  applicationKey: string;
  itemId: string;
  edited: boolean;
  label: string;
}

/** One application of a set to this visit, as the canvas blocks render it. */
export interface AppliedOrderSet {
  key: string;
  setId: string;
  setName: string;
  /** The visit diagnosis the orders are linked to; null while they wait in "Needs a diagnosis". */
  dxId: string | null;
  /** The set's saved diagnosis offered while the orders wait. */
  suggested: OrderSetDiagnosis | null;
  /** Orders waiting for a diagnosis (not on the canvas yet — the save would drop an unlinked order). */
  pending: DraftOrder[];
  optional: ListedItem[];
  duplicates: DuplicateItem[];
  notOrderable: NotOrderableItem[];
  unsupported: ListedItem[];
  listHidden: boolean;
  /**
   * The set placed a medication on this visit (now or waiting for a diagnosis). The block then
   * shows the R1 "No medication checks" notice (PROD-2721 AC1): no interaction engine runs today.
   */
  placesMedication: boolean;
  /** After Undo: what it removed, and the edited orders it kept (still on the canvas). */
  undoReceipt: { removed: number; keptOrderIds: string[]; keptLabels: string[] } | null;
}

interface ApplicationState extends AppliedOrderSet {
  items: OrderSetApplyItem[];
  /**
   * Every order this application placed on the canvas or held waiting in "Needs a diagnosis"
   * (PROD-2723 / PROD-2832: the set's list goes when they all do).
   */
  ownOrderIds: string[];
  /** Articles this application attached (by display), removed again by Undo. */
  articleDisplays: string[];
}

export interface OrderSetApplyInput {
  /** Flag on AND the user may order (physician allowlist). */
  enabled: boolean;
  encounterId?: string | null;
  patientId?: string | null;
  diagnoses: DxCard[];
  orders: PlanOrder[];
  payloads: OrderPayloadMap;
  articles: EducationArticle[];
  /** The page's single-order attach path (`handleAttachOrder`). */
  attachOrder: (dxIds: string[], planOrder: PlanOrder, payload: CodedOrderPayload) => void;
  /** Remove an order from the canvas WITHOUT the removed-before-sign event (Undo, Remove all). */
  discardOrder: (orderId: string) => void;
  addArticle: (article: EducationArticle) => void;
  removeArticleByDisplay: (display: string) => void;
  /** The page's diagnosis add path (`handleAddDx`). */
  addDx: (dx: CodeSearchResult) => 'added' | 'duplicate' | 'capped';
  /** Whether the note is signed now (events are "before sign" only; lists stay until Sign). */
  isSigned: () => boolean;
  /**
   * Record the interaction engine's alerts on a set-placed medication's payload, exactly where the
   * dose dialog puts them on confirm (`vidalAlerts`), so the row shows its interaction glyph and
   * the save sends `vidalAlertRequest`. Not an edit by the clinician.
   */
  setVidalAlerts: (orderId: string, alerts: unknown[]) => void;
}

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

/**
 * Who gets order sets on the A/P: the `VITE_ORDER_SETS_APPLY` flag on AND a user who may order
 * (`mayOrder`, the physician allowlist in auth/roles.ts — the same gate as the Settings › Order
 * sets tab). Anyone else keeps today's rail, Bundles included.
 */
export function canApplyOrderSets(flagOn: boolean, mayOrder: boolean | undefined): boolean {
  return flagOn && mayOrder === true;
}

let appSeq = 0;

export function useOrderSetApply(input: OrderSetApplyInput) {
  // No react-query hook here on purpose: the page renders (and is tested) without needing one for
  // this feature. The rail fetches the list; an apply fetches the detail directly.
  const { enabled } = input;

  const [applications, setApplications] = useState<ApplicationState[]>([]);
  const [origins, setOrigins] = useState<ReadonlyMap<string, OrderSetOrigin>>(new Map());
  const [receipt, setReceipt] = useState<{ key: string; message: string } | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const telemetry = useRef(new Map<string, OrderSetTelemetry>());
  // The latest input, read by handlers that run after an await (the apply) — never a stale closure.
  const latest = useRef(input);
  useLayoutEffect(() => {
    latest.current = input;
  });
  // The latest applications, for handlers that read them after an await (the apply's duplicate
  // check counts orders still waiting in "Needs a diagnosis" — PROD-2723).
  const appsRef = useRef<ApplicationState[]>([]);
  useLayoutEffect(() => {
    appsRef.current = applications;
  });
  // Sign waits on "Needs a diagnosis"; when the last group resolves, the sign continues.
  const pendingSign = useRef<(() => void) | null>(null);

  const emit = useCallback(
    (appKey: string, type: Parameters<OrderSetTelemetry['emit']>[0], opts?: Parameters<OrderSetTelemetry['emit']>[1]) => {
      telemetry.current.get(appKey)?.emit(type, opts);
    },
    [],
  );

  const updateApp = useCallback((key: string, fn: (a: ApplicationState) => ApplicationState | null) => {
    setApplications((prev) => prev.flatMap((a) => (a.key === key ? (fn(a) ?? []) : [a])));
  }, []);

  /** Place drafts through the single-order attach path and remember where they came from. */
  const placeDrafts = useCallback((appKey: string, setId: string, drafts: DraftOrder[], dxId: string) => {
    if (drafts.length === 0) return;
    for (const d of drafts) latest.current.attachOrder([dxId], d.planOrder, d.payload);
    const ids = drafts.map((d) => d.planOrder.id);
    setApplications((prev) =>
      prev.map((a) =>
        a.key === appKey ? { ...a, ownOrderIds: [...new Set([...a.ownOrderIds, ...ids])] } : a,
      ),
    );
    setOrigins((prev) => {
      const next = new Map(prev);
      for (const d of drafts) {
        next.set(d.planOrder.id, { orderSetId: setId, applicationKey: appKey, itemId: d.itemId, edited: false, label: d.planOrder.label });
      }
      return next;
    });
  }, []);

  /**
   * What the duplicate rules compare a set's orders against: the orders on the canvas AND the ones
   * waiting in any "Needs a diagnosis" group (PROD-2723: applying a set twice while its orders
   * wait for a diagnosis placed them twice). Not on the canvas yet, but already on this visit.
   */
  const existingOrders = useCallback(() => {
    const { orders, payloads } = latest.current;
    const waiting = appsRef.current.flatMap((a) =>
      a.undoReceipt ? [] : a.pending.map((d) => ({ label: d.planOrder.label, payload: d.payload })),
    );
    return [...orders.map((o) => ({ label: o.label, payload: payloads.get(o.id) })), ...waiting];
  }, []);

  /** Apply a set with + (no target) or by dropping it onto a diagnosis. */
  const apply = useCallback(
    async (setId: string, droppedOnDxId?: string) => {
      if (!latest.current.enabled || applyingId) return;
      setApplyingId(setId);
      setNotice(null);
      let detail: OrderSetApplyDetail;
      try {
        detail = await fetchOrderSetForApply(setId);
      } catch {
        setApplyingId(null);
        setNotice('Couldn’t load that order set. Try again.');
        return;
      }
      setApplyingId(null);
      const now = latest.current;
      const plan = planOrderSetApplication(detail, existingOrders(), now.articles);
      const decision = decideTargetDx(detail.diagnoses ?? [], now.diagnoses, droppedOnDxId);
      const key = `osa-${++appSeq}`;
      const added = plan.toAdd.length + plan.articles.length;
      const appliedItemIds = [...plan.toAdd.map((d) => d.itemId), ...plan.articles.map((a) => a.itemId)];
      telemetry.current.set(
        key,
        startOrderSetTelemetry(
          setId,
          now.encounterId && now.patientId
            ? { encounterId: now.encounterId, patientId: now.patientId, version: detail.version, appliedItemIds }
            : null,
        ),
      );

      const linkedDxId = decision.kind === 'linked' ? decision.dxId : null;
      if (linkedDxId) placeDrafts(key, setId, plan.toAdd, linkedDxId);
      for (const a of plan.articles) now.addArticle(a.article);

      // Placed (above) or waiting: either way these are this application's orders (PROD-2832 —
      // a waiting order that is removed takes the set's list with it, like a placed one).
      const ownIds = plan.toAdd.map((d) => d.planOrder.id);
      setApplications((prev) => consolidateLeftovers([
        ...prev.filter((a) => a.key !== key),
        {
          key,
          setId,
          setName: detail.name,
          dxId: linkedDxId,
          suggested: decision.kind === 'needs-dx' ? decision.suggested : null,
          pending: linkedDxId ? [] : plan.toAdd,
          optional: plan.optional,
          duplicates: plan.duplicates,
          notOrderable: plan.notOrderable,
          unsupported: plan.unsupported,
          listHidden: false,
          placesMedication: plan.toAdd.some((d) => d.payload.kind === 'medication'),
          undoReceipt: null,
          items: detail.items ?? [],
          ownOrderIds: ownIds,
          articleDisplays: plan.articles.map((a) => a.article.display),
        },
      ]));
      const notOrdered = plan.optional.length + plan.duplicates.length + plan.notOrderable.length + plan.unsupported.length;
      setReceipt({ key, message: applyReceipt(detail.name, added, notOrdered, !linkedDxId && plan.toAdd.length > 0) });
    },
    [applyingId, existingOrders, placeDrafts],
  );

  /** "Add" on an optional item, or "Order anyway" on a duplicate. */
  const addListed = useCallback(
    (appKey: string, itemId: string, kind: 'optional' | 'duplicate') => {
      const app = applications.find((a) => a.key === appKey);
      // A merged list (PROD-2723) can hold an item an earlier application of the same set listed.
      const item =
        app?.items.find((i) => i.itemId === itemId) ??
        applications.filter((a) => a.setId === app?.setId).flatMap((a) => a.items).find((i) => i.itemId === itemId);
      if (!app || !item) return;
      if (item.type === 'education') {
        const article = orderSetItemToArticle(item);
        if (!article) return;
        // Already attached (by hand, say): the A/P keeps its one copy where it is, so the set does
        // not claim it (PROD-2850 — it would move under this diagnosis, and Undo would remove it).
        const attached = latest.current.articles.some((a) => sameArticle(a, article));
        latest.current.addArticle(article);
        emit(appKey, kind === 'optional' ? 'optional_added' : 'order_anyway', { itemId });
        updateApp(appKey, (a) => ({
          ...a,
          optional: a.optional.filter((o) => o.itemId !== itemId),
          duplicates: a.duplicates.filter((o) => o.itemId !== itemId),
          articleDisplays: attached ? a.articleDisplays : [...a.articleDisplays, article.display],
        }));
        return;
      }
      const draft = orderSetItemToDraft(item, mintOrderSetOrderId(itemId));
      if (!draft) return;
      // An optional order that is ALREADY on the visit moves to the duplicates list instead of
      // being added twice — the same AC2 rules the apply used (order-set-duplicates.ts).
      if (kind === 'optional') {
        const dupOf = findDuplicateOrders(draft.payload, existingOrders());
        if (dupOf.length > 0) {
          updateApp(appKey, (a) => ({
            ...a,
            optional: a.optional.filter((o) => o.itemId !== itemId),
            duplicates: [...a.duplicates, { itemId, display: item.code.display, existingLabel: dupOf.join(', ') }],
          }));
          return;
        }
      }
      emit(appKey, kind === 'optional' ? 'optional_added' : 'order_anyway', { itemId });
      if (app.dxId) placeDrafts(appKey, app.setId, [draft], app.dxId);
      // Still waiting for a diagnosis: the order joins the set's own "Needs a diagnosis" group when
      // one is open (PROD-2832: one box per set, as one list per set), else waits on this one.
      const waitHere = app.dxId
        ? null
        : (applications.find((a) => a.setId === app.setId && !a.dxId && !a.undoReceipt && a.pending.length > 0)?.key ?? appKey);
      setApplications((prev) =>
        prev.map((a) => {
          let next = a;
          if (a.key === appKey) {
            next = {
              ...next,
              optional: next.optional.filter((o) => o.itemId !== itemId),
              duplicates: next.duplicates.filter((o) => o.itemId !== itemId),
              placesMedication: next.placesMedication || draft.payload.kind === 'medication',
            };
          }
          if (a.key === waitHere) {
            next = {
              ...next,
              pending: [...next.pending, draft],
              ownOrderIds: [...next.ownOrderIds, draft.planOrder.id],
              placesMedication: next.placesMedication || draft.payload.kind === 'medication',
            };
          }
          return next;
        }),
      );
    },
    [applications, emit, existingOrders, placeDrafts, updateApp],
  );

  const hideList = useCallback((appKey: string) => updateApp(appKey, (a) => ({ ...a, listHidden: true })), [updateApp]);

  /** Resolve a "Needs a diagnosis" group: link its waiting orders, once. */
  const resolveNeedsDx = useCallback(
    (appKey: string, how: { kind: 'suggested' } | { kind: 'dx'; dxId: string } | { kind: 'remove' }) => {
      const app = applications.find((a) => a.key === appKey);
      if (!app || app.dxId || app.pending.length === 0) return;
      let resolution: OrderSetResolution;
      let dxId: string | null = null;
      if (how.kind === 'remove') {
        resolution = 'left_unresolved';
      } else if (how.kind === 'dx') {
        resolution = 'picked_other';
        dxId = how.dxId;
      } else {
        if (!app.suggested) return;
        resolution = 'confirmed_suggested';
        const code = norm(app.suggested.code);
        const onVisit = latest.current.diagnoses.find((d) => norm(d.code) === code);
        if (onVisit) {
          dxId = onVisit.id;
        } else {
          const result = suggestedDxAsSearchResult(app.suggested);
          const outcome = latest.current.addDx(result);
          if (outcome === 'capped') return; // the page's own notice says why; the orders keep waiting
          dxId = result.id as string; // handleAddDx keys a new diagnosis by the id we passed
        }
      }
      emit(appKey, 'needs_diagnosis_resolved', { resolution, onceKey: 'needs_diagnosis_resolved' });
      if (dxId) placeDrafts(appKey, app.setId, app.pending, dxId);
      // PROD-2723: the set's other lists waiting for the same diagnosis follow it, and one list per
      // set per diagnosis remains.
      setApplications((prev) => {
        const resolved = prev.map((a) => (a.key === appKey ? { ...a, dxId: dxId ?? a.dxId, pending: [] } : a));
        return consolidateLeftovers(dxId ? followResolvedDiagnosis(resolved, app.setId, dxId) : resolved);
      });
    },
    [applications, emit, placeDrafts],
  );

  /** Remove one waiting order from a "Needs a diagnosis" group (its row's bin). */
  const removePending = useCallback(
    (appKey: string, orderId: string) => {
      const app = applications.find((a) => a.key === appKey);
      const draft = app?.pending.find((d) => d.planOrder.id === orderId);
      if (!app || !draft) return;
      if (!latest.current.isSigned()) emit(appKey, 'removed_before_sign', { itemId: draft.itemId });
      updateApp(appKey, (a) => ({ ...a, pending: a.pending.filter((d) => d.planOrder.id !== orderId) }));
    },
    [applications, emit, updateApp],
  );

  /** Undo: remove this application's unedited orders, keep (and name) the edited ones. */
  const undo = useCallback(
    (appKey: string) => {
      const app = applications.find((a) => a.key === appKey);
      if (!app || app.undoReceipt) return;
      const onCanvas = new Set(latest.current.orders.map((o) => o.id));
      const mine = [...origins.entries()].filter(([id, o]) => o.applicationKey === appKey && onCanvas.has(id));
      const removed = mine.filter(([, o]) => !o.edited);
      const kept = mine.filter(([, o]) => o.edited);
      for (const [id] of removed) latest.current.discardOrder(id);
      for (const display of app.articleDisplays) latest.current.removeArticleByDisplay(display);
      setOrigins((prev) => {
        const next = new Map(prev);
        for (const [id] of removed) next.delete(id);
        return next;
      });
      emit(appKey, 'undo', { onceKey: 'undo' });
      setReceipt((r) => (r?.key === appKey ? null : r));
      const removedCount = removed.length + app.pending.length + app.articleDisplays.length;
      if (kept.length === 0) {
        updateApp(appKey, (a) => ({
          ...a, pending: [], optional: [], duplicates: [], notOrderable: [], unsupported: [], articleDisplays: [],
          undoReceipt: { removed: removedCount, keptOrderIds: [], keptLabels: [] },
        }));
        return;
      }
      updateApp(appKey, (a) => ({
        ...a, pending: [], optional: [], duplicates: [], notOrderable: [], unsupported: [], articleDisplays: [],
        undoReceipt: { removed: removedCount, keptOrderIds: kept.map(([id]) => id), keptLabels: kept.map(([, o]) => o.label) },
      }));
    },
    [applications, emit, origins, updateApp],
  );

  /** Remove all: after Undo, also remove the edited orders it kept. */
  const removeAll = useCallback(
    (appKey: string) => {
      const app = applications.find((a) => a.key === appKey);
      if (!app?.undoReceipt || app.undoReceipt.keptOrderIds.length === 0) return;
      for (const id of app.undoReceipt.keptOrderIds) latest.current.discardOrder(id);
      setOrigins((prev) => {
        const next = new Map(prev);
        for (const id of app.undoReceipt!.keptOrderIds) next.delete(id);
        return next;
      });
      emit(appKey, 'remove_all', { onceKey: 'remove_all' });
      updateApp(appKey, () => null);
    },
    [applications, emit, updateApp],
  );

  const dismissUndoReceipt = useCallback((appKey: string) => updateApp(appKey, () => null), [updateApp]);

  /** The page tells us a set-placed order was edited (dialog save or re-link). */
  const onOrderEdited = useCallback(
    (orderId: string) => {
      const origin = origins.get(orderId);
      if (!origin || origin.edited) return;
      if (!latest.current.isSigned()) emit(origin.applicationKey, 'edited_before_sign', { itemId: origin.itemId });
      setOrigins((prev) => {
        const cur = prev.get(orderId);
        if (!cur) return prev;
        const next = new Map(prev);
        next.set(orderId, { ...cur, edited: true });
        return next;
      });
    },
    [emit, origins],
  );

  /** The page tells us a set-placed order was removed by the clinician (the row's bin). */
  const onOrderRemoved = useCallback(
    (orderId: string) => {
      const origin = origins.get(orderId);
      if (!origin) return;
      if (!latest.current.isSigned()) emit(origin.applicationKey, 'removed_before_sign', { itemId: origin.itemId });
      setOrigins((prev) => {
        const next = new Map(prev);
        next.delete(orderId);
        return next;
      });
    },
    [emit, origins],
  );

  const needsDx = applications.filter((a) => !a.dxId && a.pending.length > 0);
  const needsDxCount = needsDx.reduce((n, a) => n + a.pending.length, 0);

  // The sign that was waiting on "Needs a diagnosis" continues once the last group is resolved.
  // `proceed` must read the page's LATEST sign handler (the page passes a ref-backed callback), so
  // the continued sign sees the resolved state and is not intercepted again.
  const [signPromptOpen, setSignPromptOpen] = useState(false);
  useEffect(() => {
    if (!signPromptOpen || needsDxCount > 0 || !pendingSign.current) return;
    const proceed = pendingSign.current;
    pendingSign.current = null;
    setSignPromptOpen(false);
    proceed();
  }, [needsDxCount, signPromptOpen]);

  /**
   * Called first by the page's sign. True when it took over (orders are waiting for a diagnosis):
   * the prompt asks ONLY about those orders, and `proceed` runs once they are resolved.
   */
  const interceptSign = useCallback(
    (proceed: () => void): boolean => {
      if (!enabled || needsDxCount === 0) return false;
      pendingSign.current = proceed;
      setSignPromptOpen(true);
      return true;
    },
    [enabled, needsDxCount],
  );

  const cancelSignPrompt = useCallback(() => {
    pendingSign.current = null;
    setSignPromptOpen(false);
  }, []);

  const signed = enabled && input.isSigned();
  // PROD-2723 / PROD-2832: what each application shows NOW — its notice only while a medication of
  // it is on the visit, no list once every order its set placed or held waiting is gone, and an
  // "already ordered" chip only while that order is still on the canvas or waiting
  // (order-set-applications.ts; the duplicate rules are PROD-2721's `findDuplicateOrders`).
  const visible = useMemo(() => {
    if (!enabled) return [];
    const onVisit = [
      ...input.orders.map((o) => ({ label: o.label, payload: input.payloads.get(o.id) })),
      ...applications.flatMap((a) =>
        a.undoReceipt ? [] : a.pending.map((d) => ({ label: d.planOrder.label, payload: d.payload })),
      ),
    ];
    const itemsBySet = new Map<string, OrderSetApplyItem[]>();
    for (const a of applications) itemsBySet.set(a.setId, [...(itemsBySet.get(a.setId) ?? []), ...a.items]);
    return liveApplications(
      applications.filter((a) => !signed || a.undoReceipt),
      {
        orders: input.orders,
        payloads: input.payloads,
        isStillDuplicate: (app, dup) => {
          const item = itemsBySet.get(app.setId)?.find((i) => i.itemId === dup.itemId);
          const draft = item ? orderSetItemToDraft(item, `probe-${dup.itemId}`) : null;
          if (!draft) return true; // cannot re-check: keep what the apply said
          return findDuplicateOrders(draft.payload, onVisit).length > 0;
        },
      },
    );
  }, [applications, enabled, signed, input.orders, input.payloads]);

  // Set-placed medications still owed the dose dialog's interaction check (AC1). Once the engine
  // answers, its alerts are recorded and the check retires; a medication the clinician then edits
  // is re-checked by the dose dialog itself, as any medication is.
  const [checkedMeds, setCheckedMeds] = useState<ReadonlySet<string>>(new Set());
  const appliedMedications = useMemo(
    () =>
      enabled
        ? [...origins.entries()]
            .filter(([id, o]) => !o.edited && !checkedMeds.has(id) && input.payloads.get(id)?.kind === 'medication')
            .map(([id]) => id)
        : [],
    [enabled, origins, checkedMeds, input.payloads],
  );
  // PROD-2850 (board 6): each article a set attached, linked to the diagnosis the set was applied
  // to, as its orders are — so the A/P shows it in that diagnosis card. Client-side, like the
  // origins (an article has no diagnosis link on the wire). Kept through Sign, as the orders' links
  // are; gone with Undo (which removes the article). Waiting for a diagnosis → no link yet.
  const articleLinks = useMemo<ArticleDxLink[]>(
    () =>
      enabled
        ? applications.flatMap((a) =>
            a.undoReceipt || !a.dxId ? [] : a.articleDisplays.map((display) => ({ display, dxId: a.dxId! })),
          )
        : [],
    [applications, enabled],
  );

  const recordInteractions = useCallback((orderId: string, alerts: unknown[]) => {
    setCheckedMeds((prev) => new Set(prev).add(orderId));
    if (alerts.length > 0) latest.current.setVidalAlerts(orderId, alerts);
  }, []);

  return {
    enabled,
    applyingId,
    notice,
    apply,
    applications: visible,
    receipt: enabled ? receipt : null,
    dismissReceipt: () => setReceipt(null),
    addListed,
    hideList,
    resolveNeedsDx,
    removePending,
    undo,
    removeAll,
    dismissUndoReceipt,
    onOrderEdited,
    onOrderRemoved,
    origins,
    appliedMedications,
    recordInteractions,
    articleLinks,
    needsDx,
    needsDxCount,
    interceptSign,
    signPromptOpen,
    cancelSignPrompt,
  };
}

export type OrderSetApplyState = ReturnType<typeof useOrderSetApply>;
